/**
 * PDFImportModal - AI-powered PDF parsing for task extraction
 * Supports: Leistungsverzeichnisse, Pläne, Meeting-Protokolle, allgemeine Dokumente
 */
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, Upload, FileText, Check, AlertTriangle, Loader2, 
  Sparkles, ChevronDown, ChevronUp, Trash2, Edit2,
  CheckCircle2, XCircle, Tag
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getApiUrl } from '../utils/api';

const PDFImportModal = ({ isOpen, onClose, currentUser, selectedProject, onImportComplete }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('upload'); // upload, analyzing, preview, importing, done
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const resetModal = () => {
    setStep('upload');
    setFile(null);
    setPreviewData(null);
    setSelectedTasks([]);
    setExpandedTasks({});
    setImportResult(null);
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setError(t('pdfImport.errorNotPdf', 'Nur PDF-Dateien sind erlaubt'));
      return;
    }

    setFile(selectedFile);
    setError(null);
    setLoading(true);
    setStep('analyzing');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const url = `${getApiUrl()}/import/pdf/preview?user_id=${currentUser?.id || ''}&project_id=${selectedProject?.id || ''}`;
      
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setPreviewData(data);
        // Auto-select all tasks
        const allIndices = (data.tasks || []).map((_, idx) => idx);
        setSelectedTasks(allIndices);
        setStep('preview');
      } else {
        setError(data.detail || t('pdfImport.errorAnalyzing', 'Fehler bei der PDF-Analyse'));
        setStep('upload');
      }
    } catch (err) {
      console.error('PDF analysis error:', err);
      setError(t('pdfImport.errorUploading', 'Fehler beim Hochladen der Datei'));
      setStep('upload');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!previewData || selectedTasks.length === 0) return;

    setLoading(true);
    setStep('importing');
    setError(null);

    const tasksToImport = selectedTasks.map(idx => previewData.tasks[idx]);

    try {
      const response = await fetch(`${getApiUrl()}/import/pdf/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: selectedProject?.id,
          user_id: currentUser?.id,
          tasks: tasksToImport,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setImportResult(data);
        setStep('done');
        if (onImportComplete) {
          onImportComplete(data);
        }
      } else {
        setError(data.detail || t('pdfImport.errorImporting', 'Fehler beim Import'));
        setStep('preview');
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(t('pdfImport.errorImporting', 'Fehler beim Import'));
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskSelection = (idx) => {
    setSelectedTasks(prev => 
      prev.includes(idx) 
        ? prev.filter(i => i !== idx)
        : [...prev, idx]
    );
  };

  const toggleAllTasks = () => {
    if (selectedTasks.length === previewData?.tasks?.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(previewData?.tasks?.map((_, idx) => idx) || []);
    }
  };

  const toggleTaskExpanded = (idx) => {
    setExpandedTasks(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'leistungsverzeichnis': t('pdfImport.docType.specifications', 'Leistungsverzeichnis'),
      'bauplan': t('pdfImport.docType.blueprint', 'Technischer Plan'),
      'protokoll': t('pdfImport.docType.protocol', 'Meeting-Protokoll'),
      'sonstiges': t('pdfImport.docType.document', 'Dokument')
    };
    return labels[type] || type;
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      'leistungsverzeichnis': 'bg-blue-500',
      'bauplan': 'bg-emerald-500',
      'protokoll': 'bg-purple-500',
      'sonstiges': 'bg-gray-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold dark:text-white">
                {t('pdfImport.title', 'PDF AI-Import')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('pdfImport.subtitle', 'Tasks aus PDF-Dokumenten extrahieren')}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            data-testid="pdf-import-close-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-red-700 dark:text-red-300">{error}</div>
            </div>
          )}

          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-12 
                         hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/10 
                         transition-all cursor-pointer group"
                data-testid="pdf-upload-dropzone"
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full 
                                group-hover:bg-purple-200 dark:group-hover:bg-purple-800/40 transition-colors">
                    <Upload className="w-10 h-10 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-medium dark:text-white">
                      {t('pdfImport.dropzone', 'PDF-Datei hier ablegen oder klicken')}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('pdfImport.supportedTypes', 'Leistungsverzeichnisse, Pläne, Protokolle, etc.')}
                    </p>
                  </div>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="pdf-file-input"
              />

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 
                            rounded-xl p-4 border border-purple-100 dark:border-purple-800">
                <h3 className="font-medium text-purple-900 dark:text-purple-100 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  {t('pdfImport.howItWorks', 'So funktioniert es')}
                </h3>
                <ul className="text-sm text-purple-800 dark:text-purple-200 space-y-1">
                  <li>• {t('pdfImport.step1', 'PDF hochladen')}</li>
                  <li>• {t('pdfImport.step2', 'KI analysiert das Dokument und extrahiert Tasks')}</li>
                  <li>• {t('pdfImport.step3', 'Vorschau prüfen und Tasks auswählen')}</li>
                  <li>• {t('pdfImport.step4', 'Ausgewählte Tasks importieren')}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step: Analyzing */}
          {step === 'analyzing' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-400 rounded-full animate-ping opacity-20" />
                <div className="relative p-6 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full">
                  <Sparkles className="w-12 h-12 text-white animate-pulse" />
                </div>
              </div>
              <h3 className="mt-6 text-xl font-bold dark:text-white">
                {t('pdfImport.analyzing', 'KI analysiert PDF...')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                {file?.name}
              </p>
              <div className="flex items-center gap-2 mt-4 text-sm text-purple-600 dark:text-purple-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('pdfImport.extractingTasks', 'Tasks werden extrahiert...')}
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && previewData && (
            <div className="space-y-4">
              {/* Document Info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <FileText className="w-8 h-8 text-gray-400" />
                    <div>
                      <h3 className="font-medium dark:text-white">{previewData.filename}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {previewData.page_count} {t('pdfImport.pages', 'Seiten')}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${getDocumentTypeColor(previewData.document_type)} text-white`}>
                    {getDocumentTypeLabel(previewData.document_type)}
                  </Badge>
                </div>
                {previewData.document_summary && (
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 border-t dark:border-gray-600 pt-3">
                    {previewData.document_summary}
                  </p>
                )}
              </div>

              {/* Tasks Header */}
              <div className="flex items-center justify-between">
                <h3 className="font-bold dark:text-white flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  {previewData.tasks?.length || 0} {t('pdfImport.tasksFound', 'Tasks gefunden')}
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleAllTasks}
                  data-testid="pdf-toggle-all-tasks"
                >
                  {selectedTasks.length === previewData.tasks?.length 
                    ? t('pdfImport.deselectAll', 'Alle abwählen')
                    : t('pdfImport.selectAll', 'Alle auswählen')}
                </Button>
              </div>

              {/* Tasks List */}
              {previewData.tasks?.length > 0 ? (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {previewData.tasks.map((task, idx) => (
                    <div 
                      key={idx}
                      className={`border rounded-xl transition-all ${
                        selectedTasks.includes(idx)
                          ? 'border-purple-300 bg-purple-50 dark:border-purple-600 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/30'
                      }`}
                    >
                      <div 
                        className="p-3 flex items-start gap-3 cursor-pointer"
                        onClick={() => toggleTaskSelection(idx)}
                        data-testid={`pdf-task-${idx}`}
                      >
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          selectedTasks.includes(idx)
                            ? 'bg-purple-500 border-purple-500'
                            : 'border-gray-300 dark:border-gray-500'
                        }`}>
                          {selectedTasks.includes(idx) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium dark:text-white">{task.title}</h4>
                            {task.priority === 'urgent' && (
                              <Badge variant="destructive" className="flex-shrink-0">
                                {t('pdfImport.urgent', 'Dringend')}
                              </Badge>
                            )}
                          </div>
                          {task.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {task.tags.map((tag, tagIdx) => (
                                <Badge key={tagIdx} variant="secondary" className="text-xs">
                                  <Tag className="w-3 h-3 mr-1" />
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTaskExpanded(idx);
                          }}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                          {expandedTasks[idx] ? (
                            <ChevronUp className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                      
                      {/* Expanded Details */}
                      {expandedTasks[idx] && (
                        <div className="px-3 pb-3 pt-0 ml-8 border-t dark:border-gray-600 mt-2">
                          {task.description && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t('pdfImport.description', 'Beschreibung')}:
                              </p>
                              <p className="text-sm dark:text-gray-300">{task.description}</p>
                            </div>
                          )}
                          {task.source_text && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t('pdfImport.sourceText', 'Originaltext')}:
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 
                                          p-2 rounded mt-1 font-mono">
                                {task.source_text}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <XCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('pdfImport.noTasksFound', 'Keine Tasks im Dokument gefunden')}</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-16 h-16 text-purple-500 animate-spin" />
              <h3 className="mt-6 text-xl font-bold dark:text-white">
                {t('pdfImport.importing', 'Tasks werden importiert...')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                {selectedTasks.length} {t('pdfImport.tasksImporting', 'Tasks')}
              </p>
            </div>
          )}

          {/* Step: Done */}
          {step === 'done' && importResult && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="p-4 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                <CheckCircle2 className="w-16 h-16 text-emerald-500" />
              </div>
              <h3 className="mt-6 text-xl font-bold dark:text-white">
                {t('pdfImport.success', 'Import erfolgreich!')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                {importResult.created_count} {t('pdfImport.tasksCreated', 'Tasks erstellt')}
              </p>
              
              {importResult.errors?.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm">
                  <p className="text-yellow-700 dark:text-yellow-300">
                    {importResult.error_count} {t('pdfImport.errorsDuringImport', 'Fehler beim Import')}
                  </p>
                </div>
              )}

              <Button 
                onClick={handleClose}
                className="mt-6 bg-emerald-500 hover:bg-emerald-600"
                data-testid="pdf-import-done-btn"
              >
                {t('pdfImport.close', 'Schließen')}
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        {(step === 'preview') && (
          <div className="border-t dark:border-gray-700 p-4 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50">
            <Button
              variant="outline"
              onClick={resetModal}
              data-testid="pdf-import-back-btn"
            >
              {t('pdfImport.back', 'Zurück')}
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedTasks.length} {t('pdfImport.selected', 'ausgewählt')}
              </span>
              <Button
                onClick={handleImport}
                disabled={selectedTasks.length === 0 || loading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="pdf-import-execute-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {t('pdfImport.importTasks', 'Tasks importieren')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFImportModal;
